import React, {forwardRef, memo} from 'react';
import {twMerge} from 'tailwind-merge';
import {menuRowSizes} from 'utils/uiSizes';

/**
 * MenuRow - base row primitive for menu-like items
 * Provides consistent sizing and layout; callers control semantics and states.
 *
 * @param {Object} props
 * @param {string} [props.as] - Element tag to render (e.g., 'div', 'button', 'a')
 * @param {string} [props.size] - Size variant: 'xs' | 'sm' | 'md' | 'lg'
 * @param {('default'|'pill')} [props.variant] - Corner style variant
 * @param {('neutral'|'accent')} [props.palette] - Color palette for hover/active
 * @param {boolean} [props.active] - Active state styling
 * @param {boolean} [props.highlighted] - Highlighted (focus/keyboard) styling
 * @param {boolean} [props.disabled] - Disabled state styling
 * @param {string} [props.className] - Additional classes to merge
 * @param {React.ReactNode} props.children - Row content
 */
const MenuRow = memo(
	forwardRef(
		(
			{
				as = 'div',
				size = 'md',
				variant = 'default',
				active = false,
				highlighted = false,
				disabled = false,
				className,
				children,
				...props
			},
			ref,
		) => {
			const Component = as;
			const sizeClasses = menuRowSizes[size];
			const roundedClass = variant === 'pill' ? 'rounded' : 'rounded-none';
			const hoverClasses =
				'hover:bg-blue-50 dark:hover:bg-blue-900/30 hover:text-gray-900 dark:hover:text-gray-200';
			const stateClasses = twMerge(
				disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer',
				active &&
					'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 hover:text-blue-700 dark:hover:text-blue-300',
				active &&
					disabled &&
					'bg-transparent opacity-80 text-gray-400 dark:text-gray-500 hover:bg-transparent hover:text-gray-400 dark:hover:text-gray-500',
				highlighted &&
					!disabled &&
					'ring-inset ring-1 ring-blue-500/50 dark:ring-blue-400/50 bg-blue-50 dark:bg-blue-900/30',
			);
			return (
				<Component
					ref={ref}
					className={twMerge(
						'flex items-center group',
						'text-gray-700 dark:text-gray-300',
						'transition-colors',
						'focus:outline-none',
						roundedClass,
						sizeClasses,
						!disabled && hoverClasses,
						stateClasses,
						className,
					)}
					{...props}
				>
					{children}
				</Component>
			);
		},
	),
);

MenuRow.displayName = 'MenuRow';

export default MenuRow;
